import React, { useState, useEffect } from 'react';
import { X, Search, Clock, Plus, Check, ChevronDown, Trash2 } from 'lucide-react';
import { getFlatRateItems, FlatRateItem } from '../lib/flat-rates';
import { useBusiness } from '../contexts/BusinessContext';
import { Resource } from '../lib/resources';
import { supabase } from '../lib/supabase';
import FlatRateModal from './FlatRateModal';
import { selectInputContentOnFocus } from '../lib/utils';

interface SelectFlatRateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (item: FlatRateItem, hours: number, resourceId: string) => Promise<void>;
  selectedResourceId?: string | null;
  bookingId?: string;
}

export default function SelectFlatRateModal({
  isOpen,
  onClose,
  onAdd,
  selectedResourceId,
  bookingId
}: SelectFlatRateModalProps) {
  const { selectedBusiness, role } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [items, setItems] = useState<FlatRateItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [resources, setResources] = useState<Resource[]>([]);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [selectedItemsData, setSelectedItemsData] = useState<Map<string, {
    item: FlatRateItem;
    hours: number;
    originalHours: number;
  }>>(new Map());
  const [isResourceSelectorOpen, setIsResourceSelectorOpen] = useState(false);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [addedItemIds, setAddedItemIds] = useState<Set<string>>(new Set());

  // Check if current user is a resource
  const checkCurrentResource = async () => {
    if (!supabase) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const { data: resource } = await supabase
        .from('resources')
        .select('*')
        .eq('auth_user_id', session.user.id)
        .single();

      if (resource) {
        setSelectedResource(resource);
      }
    } catch (err) {
      console.error('Error checking current resource:', err);
    }
  };

  const loadItems = async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      setLoading(true);
      const data = await getFlatRateItems(selectedBusiness.id);
      setItems(data);
      setError(null);
    } catch (err) {
      console.error('Error loading flat rate items:', err);
      setError(err instanceof Error ? err.message : 'Failed to load flat rate items');
    } finally {
      setLoading(false);
    }
  };

  // Load currently added items for this booking
  const loadAddedItems = async () => {
    if (!bookingId || !supabase) return;

    try {
      const { data: addedItems, error } = await supabase
        .from('booking_flat_rates_with_details')
        .select('item_id')
        .eq('booking_id', bookingId);

      if (error) throw error;

      // Get unique item IDs that have been added to this booking
      const itemIds = new Set(addedItems?.map(item => item.item_id) || []);
      setAddedItemIds(itemIds);
    } catch (err) {
      console.error('Error loading added items:', err);
      // Don't show error to user, just log it
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      Promise.all([loadItems(), loadResources(), checkCurrentResource(), loadAddedItems()]);
    }
  }, [isOpen, selectedBusiness?.id, bookingId]);

  const loadResources = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      // Only get active resources
      const { data, error } = await supabase
        .from('resources')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .eq('status', 'active')
        .order('first_name');

      if (error) throw error;
      setResources(data);

      // If selectedResourceId is provided, pre-select that resource
      if (selectedResourceId) {
        const resource = data.find(r => r.id === selectedResourceId);
        if (resource) {
          setSelectedResource(resource);
        }
      }
    } catch (err) {
      console.error('Error loading resources:', err);
      setError(err instanceof Error ? err.message : 'Failed to load resources');
    }
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setSearchQuery('');
      setSelectedItems(new Set());
      setSelectedResource(null);
      setSelectedItemsData(new Map());

    }
  };

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  // Group items by category
  const itemsByCategory = React.useMemo(() => {
    const grouped = items.reduce((acc, item) => {
      const categoryId = item.category_id || 'uncategorized';
      if (!acc[categoryId]) {
        acc[categoryId] = {
          id: categoryId,
          name: item.category?.name || 'Uncategorized',
          items: []
        };
      }
      acc[categoryId].items.push(item);
      return acc;
    }, {} as Record<string, { id: string; name: string; items: FlatRateItem[] }>);

    return Object.values(grouped).sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  const filteredCategories = itemsByCategory.filter(category => {
    if (!searchQuery) return true;
    
    const searchLower = searchQuery.toLowerCase();
    return (
      category.name.toLowerCase().includes(searchLower) ||
      category.items.some(item => 
        item.name.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower)
      )
    );
  });

  const handleAddItem = (item: FlatRateItem) => {
    if (selectedItems.has(item.id)) {
      setSelectedItems(prev => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
      setSelectedItemsData(prev => {
        const next = new Map(prev);
        next.delete(item.id);
        return next;
      });
    } else {
      setSelectedItems(prev => {
        const next = new Set(prev);
        next.add(item.id);
        return next;
      });
      setSelectedItemsData(prev => {
        const next = new Map(prev);
        next.set(item.id, {
          item,
          hours: parseFloat(item.hours.toString()),
          originalHours: item.hours
        });
        return next;
      });
    }
  };

  const handleSubmit = async () => {
    try {
      if (!selectedResource && role === 'admin') {
        throw new Error('Please select a resource to assign these items to');
      }

      // Process each item
      for (const {item, hours, originalHours} of selectedItemsData.values()) {
        // Validate hours
        const finalHours = parseFloat(hours.toString()) || originalHours;
        if (isNaN(finalHours) || finalHours <= 0) {
          throw new Error(`Invalid hours value for ${item.name}`);
        }
        
        await onAdd(item, finalHours, selectedResource?.id || 'unassigned');
      }

      // Clear selections and reload added items to update badges
      setSelectedItems(new Set());
      setSelectedItemsData(new Map());
      await loadAddedItems();
      
      // Only close modal after all items are added successfully
      onClose();
    } catch (err) {
      console.error('Error adding flat rate items:', err);
      setError(err instanceof Error ? err.message : 'Failed to add flat rate items');
    }
  };

  if (!isOpen && !isAnimating) return null;

  return (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-50`}
      onClick={onClose}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          <div className="px-6 py-4 border-b border-[#2f2f2f] bg-[#1a1a1a] flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Select Flat Rate Item</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {/* Only show resource selector for admin users */}
            {role === 'admin' && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assign Resource *
                </label>
                <div className="relative">
                  <button
                    onClick={() => setIsResourceSelectorOpen(!isResourceSelectorOpen)}
                    className={`w-full text-left px-3 py-2 text-sm bg-gray-50 border ${
                      !selectedResource && 'border-red-300'
                    } border-gray-300 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-900`}
                  >
                    {selectedResource ? (
                      <span>{selectedResource.first_name} {selectedResource.last_name}</span>
                    ) : (
                      <span className="text-red-500">Select a resource (required)</span>
                    )}
                  </button>
                  
                  {isResourceSelectorOpen && (
                    <div className="absolute left-0 mt-1 w-full bg-white rounded-md shadow-lg border border-gray-200 py-1 z-10">
                      {resources.map((resource) => (
                        <button
                          key={resource.id}
                          onClick={() => {
                            setSelectedResource(resource);
                            setIsResourceSelectorOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                        >
                          {resource.first_name} {resource.last_name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                  <p className="mt-4 text-sm text-gray-600">Loading items...</p>
                </div>
              </div>
            ) : error ? (
              <div className="text-center py-6">
                <div className="text-red-600 mb-2">{error}</div>
                <button
                  onClick={loadItems}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Try again
                </button>
              </div>
            ) : filteredCategories.length === 0 ? (
              <div className="text-center py-12">
                <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No items found</h3>
                <p className="mt-2 text-sm text-gray-500">
                  {searchQuery ? 'Try adjusting your search terms' : 'Add flat rate items to get started'}
                </p>
                {!searchQuery && (
                  <button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="mt-4 inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Flat Rate Item
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                {filteredCategories.map((category) => (
                  <div key={category.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div 
                      className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between cursor-pointer"
                      onClick={() => toggleCategory(category.id)}
                    >
                      <div className="flex items-center space-x-2">
                        <ChevronDown 
                          className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                            expandedCategories.has(category.id) ? 'rotate-180' : ''
                          }`}
                        />
                        <h3 className="text-sm font-medium text-gray-900">{category.name}</h3>
                        <span className="text-xs text-gray-500">({category.items.length})</span>
                      </div>
                    </div>
                    {expandedCategories.has(category.id) && (
                      <div className="divide-y divide-gray-200">
                        {category.items.map((item) => (
                          <div key={item.id} className="p-4 hover:bg-gray-50">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h4 className="text-sm font-medium text-gray-900">{item.name}</h4>
                                  {addedItemIds.has(item.id) && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      <Check className="h-3 w-3 mr-1" />
                                      Added
                                    </span>
                                  )}
                                </div>
                                {item.description && (
                                  <p className="text-xs text-gray-500 mt-1">{item.description}</p>
                                )}
                                <div className="mt-2 flex items-center text-xs text-gray-500">
                                  <Clock className="h-3.5 w-3.5 mr-1" />
                                  {item.hours} hours
                                </div>
                              </div>
                              <button
                                onClick={() => handleAddItem(item)}
                                disabled={selectedItems.has(item.id)}
                                className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                  selectedItems.has(item.id) 
                                    ? 'bg-green-100 text-green-800 cursor-not-allowed'
                                    : 'text-white bg-gray-900 hover:bg-gray-800'
                                }`}
                              >
                                {selectedItems.has(item.id) ? (
                                  <>
                                    <Check className="h-3.5 w-3.5 mr-1.5" />
                                    Added
                                  </>
                                ) : (
                                  <>
                                    <Plus className="h-3.5 w-3.5 mr-1.5" />
                                    Add
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {selectedItems.size > 0 && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-gray-900">
                    Selected Items ({selectedItemsData.size})
                  </span>
                  <button
                    onClick={handleSubmit}
                    disabled={selectedItemsData.size === 0}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800"
                  >
                    Add Selected Items
                  </button>
                </div>
                {error && (
                  <div className="mb-4 text-sm text-red-600">
                    {error}
                  </div>
                )}
                <div className="space-y-2">
                  {Array.from(selectedItemsData.values()).map(({item, hours, originalHours}) => (
                    <div key={item.id} className="flex items-center justify-between text-sm">
                      <div className="flex-1">
                        <span className="text-gray-600">{item.name}</span>
                        <div className="mt-1 flex items-center space-x-2">
                          <input
                            type="number"
                            min="0.1"
                            step="0.1"
                            value={hours}
                            onChange={(e) => {
                              const newHours = parseFloat(e.target.value);
                              if (newHours > 0) {
                                setSelectedItemsData(prev => {
                                  const next = new Map(prev);
                                  next.set(item.id, {
                                    item,
                                    hours: newHours,
                                    originalHours: item.hours
                                  });
                                  return next;
                                });
                              }
                            }}
                            onFocus={selectInputContentOnFocus}
                            className="w-20 h-7 px-2 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-900"
                          />
                          <span className="text-xs text-gray-500">hours</span>
                          {hours !== originalHours && (
                            <span className="text-xs text-gray-500">
                              (Default: {originalHours}h)
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddItem(item);
                        }}
                        className="ml-2 p-1 text-red-400 hover:text-red-600 rounded-md hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <FlatRateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          setIsCreateModalOpen(false);
          loadItems();
        }}
        businessId={selectedBusiness?.id}
        mode="create"
      />
    </div>
  );
}